import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { changePassword, clearAuthSession, getUserDashboard, updateUserProfile } from '../services/authApi';
import {
  CreditCard, LayoutList, User, Lock, LogOut,
  CheckCircle2
} from 'lucide-react';

function getSubscriptionStatusDisplay(status) {
  if (status === 'active') {
    return {
      label: 'ACTIVE',
      className: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    };
  }

  if (status === 'pending_activation') {
    return {
      label: 'PENDING',
      className: 'bg-amber-500/10 text-amber-300 border-amber-500/25',
    };
  }

  return {
    label: status ? String(status).replace(/_/g, ' ').toUpperCase() : 'N/A',
    className: 'bg-slate-500/10 text-slate-300 border-slate-500/25',
  };
}

export default function MyAccount() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Payments');
  const [dashboard, setDashboard] = useState(null);
  const [dashboardError, setDashboardError] = useState('');
  const [isDashboardLoading, setIsDashboardLoading] = useState(true);
  const [profileMessage, setProfileMessage] = useState('');

  // Account Details form state
  const [details, setDetails] = useState({ firstName: '', lastName: '', displayName: '', email: '' });
  const [detailErrors, setDetailErrors] = useState({});
  const [detailSubmitted, setDetailSubmitted] = useState(false);

  // Password form state
  const [passwords, setPasswords] = useState({ current: '', newPass: '', confirm: '' });
  const [passErrors, setPassErrors] = useState({});
  const [passSubmitted, setPassSubmitted] = useState(false);
  const [passMessage, setPassMessage] = useState('');
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);

  useEffect(() => {
    const loadDashboard = async () => {
      const token = localStorage.getItem('authToken');

      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const data = await getUserDashboard();
        const displayName = data.user_meta?.display_name || `${data.firstname || ''} ${data.lastname || ''}`.trim();

        setDashboard(data);
        setDetails({
          firstName: data.firstname || '',
          lastName: data.lastname || '',
          displayName: displayName || '',
          email: data.email || '',
        });
      } catch (error) {
        setDashboardError(error.message);

        if (error.message === 'Unauthorized') {
          clearAuthSession();
          navigate('/login');
        }
      } finally {
        setIsDashboardLoading(false);
      }
    };

    loadDashboard();
  }, [navigate]);

  const fullName = `${details.firstName} ${details.lastName}`.trim() || 'Account User';
  const initials = fullName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'AU';

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(date));
  };

  const formatAmount = (amount) => {
    const value = Number(amount || 0);
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR' }).format(value);
  };

  const latestSubscription = dashboard?.subscriptions?.[0];
  const payments = dashboard?.payments || [];
  const latestSubscriptionStatus = getSubscriptionStatusDisplay(latestSubscription?.status);

  const validateDetails = () => {
    const errs = {};
    if (!details.firstName.trim()) errs.firstName = 'First name is required.';
    if (!details.lastName.trim()) errs.lastName = 'Last name is required.';
    if (!details.displayName.trim()) errs.displayName = 'Display name is required.';
    if (!details.email.trim()) errs.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(details.email)) errs.email = 'Enter a valid email address.';
    return errs;
  };

  const handleDetailSubmit = async (e) => {
    e.preventDefault();
    const errs = validateDetails();
    setDetailErrors(errs);
    setDetailSubmitted(true);
    setProfileMessage('');
    if (Object.keys(errs).length === 0) {
      try {
        await updateUserProfile({
          firstname: details.firstName.trim(),
          lastname: details.lastName.trim(),
          display_name: details.displayName.trim(),
        });

        setProfileMessage('Profile updated successfully.');
        setDashboard((current) => current ? {
          ...current,
          firstname: details.firstName.trim(),
          lastname: details.lastName.trim(),
          user_meta: {
            ...current.user_meta,
            display_name: details.displayName.trim(),
          },
        } : current);
      } catch (error) {
        setProfileMessage(error.message);
      }
    }
  };

  const validatePasswords = () => {
    const errs = {};
    if (!passwords.current) errs.current = 'Current password is required.';
    if (!passwords.newPass) errs.newPass = 'New password is required.';
    else if (passwords.newPass.length < 6) errs.newPass = 'Must be at least 6 characters.';
    if (!passwords.confirm) errs.confirm = 'Please confirm your new password.';
    else if (passwords.newPass !== passwords.confirm) errs.confirm = 'Passwords do not match.';
    return errs;
  };

  const handlePassSubmit = async (e) => {
    e.preventDefault();
    const errs = validatePasswords();
    setPassErrors(errs);
    setPassSubmitted(true);
    setPassMessage('');
    if (Object.keys(errs).length === 0) {
      setIsPasswordLoading(true);

      try {
        await changePassword({
          old_password: passwords.current,
          new_password: passwords.newPass,
        });

        setPasswords({ current: '', newPass: '', confirm: '' });
        setPassSubmitted(false);
        setPassMessage('Password updated successfully.');
      } catch (error) {
        setPassMessage(error.message);
      } finally {
        setIsPasswordLoading(false);
      }
    }
  };

  const inputClass = "w-full px-5 py-4 bg-[#0F172A] border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-[#2563EB] focus:ring-1 focus:ring-[#2563EB] transition-all outline-none shadow-inner";
  const inputErrClass = "w-full px-5 py-4 bg-[#0F172A] border border-[#F43F5E]/60 rounded-xl text-white placeholder-slate-500 focus:border-[#F43F5E] focus:ring-1 focus:ring-[#F43F5E] transition-all outline-none shadow-inner";
  const labelClass = "block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2";

  return (
    <div className="flex flex-col min-h-screen font-sans text-white bg-[#0F172A]  relative">

      {/* GLOBAL BACKGROUND GLOWS */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        <img src="https://images.unsplash.com/photo-1639322537228-f710d846310a?auto=format&fit=crop&q=80&w=2400" alt="Background" className="w-full h-full object-cover opacity-[0.03] mix-blend-screen" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/80 via-[#0F172A]/95 to-[#0F172A]" />
        <div className="absolute top-0 right-1/4 w-[600px] h-[600px] bg-[#2563EB]/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-[#F43F5E]/10 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-50">
        <Header />
      </div>

      {/* PAGE HEADER */}
      <div className="relative pt-16 max-w-5xl mx-auto w-full px-4 z-10 text-center pb-8 pt-40 ">
        <h1 className="text-4xl font-extrabold tracking-tight md:text-5xl text-white mb-3">
          Account <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#F43F5E]">Settings</span>
        </h1>
        <p className="text-slate-400 text-lg">Manage your subscription, payments, and security.</p>
      </div>

      {/* MAIN LAYOUT (Single Column) */}
      <div className="flex flex-col flex-1 w-full max-w-5xl mx-auto px-4 relative z-10 pb-32 space-y-6">

        {/* TOP PROFILE & LOGOUT BAR */}
        <div className="bg-[#1E293B] p-6 rounded-[2rem] border border-slate-700/80 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 shadow-xl">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-[#2563EB] text-white flex items-center justify-center font-black text-xl shrink-0 shadow-[0_0_15px_rgba(37,99,235,0.4)]">
              {initials}
            </div>
            <div className="overflow-hidden">
              <div className="font-bold text-lg text-white truncate">{fullName}</div>
              <div className="text-slate-400 text-sm truncate">{details.email || 'Loading account...'}</div>
            </div>
          </div>
          <button onClick={() => { clearAuthSession(); navigate('/'); }} className="flex items-center gap-2 px-5 py-2.5 bg-[#0F172A] border border-slate-700 text-[#F43F5E] font-bold rounded-xl hover:bg-[#F43F5E]/10 transition-colors">
            <LogOut className="w-4 h-4" />
            Log Out
          </button>
        </div>

        {/* HORIZONTAL TAB NAVIGATION */}
        <nav className="bg-[#1E293B] p-2 rounded-2xl border border-slate-700/80 shadow-xl flex overflow-x-auto">
          {[
            { id: 'Payments', icon: CreditCard, label: 'Payments' },
            { id: 'Subscriptions', icon: LayoutList, label: 'My Subscriptions' },
            { id: 'Details', icon: User, label: 'Account Details' },
            { id: 'Password', icon: Lock, label: 'Change Password' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-bold transition-all duration-300 whitespace-nowrap ${activeTab === tab.id
                  ? 'bg-[#2563EB] text-white shadow-[0_0_15px_rgba(37,99,235,0.3)]'
                  : 'text-slate-400 hover:text-white hover:bg-[#0F172A]'
                }`}
            >
              <tab.icon className={`w-4 h-4 ${activeTab === tab.id ? 'text-white' : 'text-slate-500'}`} />
              {tab.label}
            </button>
          ))}
        </nav>

        {/* MAIN CONTENT AREA */}
        <div className="text-left bg-[#1E293B] p-8 md:p-12 rounded-[2.5rem] border border-slate-700/80 shadow-2xl min-h-[500px] relative overflow-hidden">

          {/* Subtle top border highlight */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-[#2563EB] opacity-80 z-20" />

          {isDashboardLoading && (
            <div className="mb-6 text-sm text-slate-400">Loading account details...</div>
          )}

          {dashboardError && (
            <div className="mb-6 text-sm text-[#F43F5E]">{dashboardError}</div>
          )}

          {/* TAB: PAYMENTS */}
          {activeTab === 'Payments' && (
            <div className="space-y-8 animate-in fade-in duration-300">
              <h2 className="text-3xl font-bold text-white">Payment History</h2>
              <div className="overflow-x-auto rounded-2xl border border-slate-700/80 bg-[#0F172A]">
                <table className="w-full text-left text-sm whitespace-nowrap">
                  <thead className="text-slate-400 uppercase tracking-widest font-bold border-b border-slate-700/80 text-[10px]">
                    <tr>
                      <th className="p-6">Transaction ID</th>
                      <th className="p-6">Date</th>
                      <th className="p-6">Status</th>
                      <th className="p-6 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700/80">
                    {payments.length > 0 ? payments.map((payment, index) => (
                      <tr key={`${payment.payment_id || 'payment'}-${index}`} className="hover:bg-slate-800/50 transition-colors">
                        <td className="p-6 font-mono text-[#2563EB]">{payment.payment_id || 'N/A'}</td>
                        <td className="p-6 text-slate-300">{formatDate(payment.date)}</td>
                        <td className="p-6">
                          <span className={`inline-flex items-center gap-1.5 border font-bold text-[10px] px-3 py-1 rounded-lg uppercase tracking-wider ${latestSubscriptionStatus.className}`}>
                            <CheckCircle2 className="w-3 h-3" /> {latestSubscriptionStatus.label}
                          </span>
                        </td>
                        <td className="p-6 font-bold text-lg text-right text-white">{formatAmount(payment.price)}</td>
                      </tr>
                    )) : (
                      <tr>
                        <td className="p-6 text-slate-400" colSpan="4">No payment history found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB: SUBSCRIPTIONS */}
          {activeTab === 'Subscriptions' && (
            <div className="space-y-12 animate-in fade-in duration-300">
              <h2 className="text-3xl font-bold text-white">My Subscriptions</h2>

              <div className="border border-slate-700/80 bg-[#0F172A] rounded-3xl divide-y divide-slate-700/80 overflow-hidden">
                {[
                  { label: 'Status', value: <span className={`inline-flex items-center rounded-lg border px-3 py-1 text-[10px] font-bold tracking-wider ${latestSubscriptionStatus.className}`}>{latestSubscriptionStatus.label}</span> },
                  { label: 'Start Date', value: formatDate(latestSubscription?.starts_at) },
                  { label: 'Next Billing', value: formatDate(latestSubscription?.ends_at) },
                  { label: 'Last Payment', value: latestSubscription?.payment_status || 'N/A' },
                  { label: 'Current Plan', value: <span className="text-[#2563EB] font-bold">{latestSubscription?.plan_name || 'No active plan'}</span> },
                ].map((row, i) => (
                  <div key={i} className="flex justify-between items-center p-6 hover:bg-slate-800/30 transition-colors">
                    <span className="text-slate-400 font-medium">{row.label}</span>
                    <span className="text-white">{row.value}</span>
                  </div>
                ))}
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Totals */}
                <div className="space-y-6">
                  <h3 className="text-xl font-bold text-white">Subscription Totals</h3>
                  <div className="border border-slate-700/80 bg-[#0F172A] rounded-3xl divide-y divide-slate-700/80 p-6">
                    <div className="flex justify-between py-3">
                      <span className="text-slate-400">{latestSubscription?.plan_name || 'Plan'}</span>
                      <span className="font-bold text-white">{latestSubscription ? formatAmount(latestSubscription.price) : formatAmount(0)}</span>
                    </div>
                    <div className="flex justify-between py-3">
                      <span className="text-slate-400">Taxes</span>
                      <span className="font-bold text-white">EUR 0.00</span>
                    </div>
                    <div className="flex justify-between py-3 mt-2 border-t border-slate-700/80">
                      <span className="font-bold text-white">Total</span>
                      <span className="font-bold text-[#2563EB] text-lg">{latestSubscription ? formatAmount(latestSubscription.price) : formatAmount(0)} / {latestSubscription?.billing_frequency || 'mo'}</span>
                    </div>
                  </div>
                </div>

                {/* Billing Info */}
                <div className="space-y-6">
                  <h3 className="text-xl font-bold text-white">Billing Address</h3>
                  <div className="border border-slate-700/80 bg-[#0F172A] rounded-3xl p-6 text-sm text-slate-300 leading-relaxed">
                    <p className="font-bold text-white mb-2">{fullName}</p>
                    <p>{dashboard?.address || 'Address not added'}</p>
                    <p>{dashboard?.city || 'City not added'}</p>
                    <p>{dashboard?.postal_code || 'Postal code not added'}</p>
                    <p>{dashboard?.country || 'Country not added'}</p>
                    <p className="mt-4 text-[#2563EB]">{details.email || 'Email not available'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB: ACCOUNT DETAILS */}
          {activeTab === 'Details' && (
            <div className="space-y-8 animate-in fade-in duration-300">
              <h2 className="text-3xl font-bold text-white">Account Details</h2>

              <form className="space-y-6" onSubmit={handleDetailSubmit} noValidate>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={labelClass}>First Name <span className="text-[#F43F5E]">*</span></label>
                    <input type="text" value={details.firstName} onChange={(e) => setDetails({ ...details, firstName: e.target.value })} className={detailSubmitted && detailErrors.firstName ? inputErrClass : inputClass} />
                    {detailSubmitted && detailErrors.firstName && <p className="text-[#F43F5E] text-xs mt-1.5">{detailErrors.firstName}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Last Name <span className="text-[#F43F5E]">*</span></label>
                    <input type="text" value={details.lastName} onChange={(e) => setDetails({ ...details, lastName: e.target.value })} className={detailSubmitted && detailErrors.lastName ? inputErrClass : inputClass} />
                    {detailSubmitted && detailErrors.lastName && <p className="text-[#F43F5E] text-xs mt-1.5">{detailErrors.lastName}</p>}
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Display Name</label>
                  <input type="text" value={details.displayName} onChange={(e) => setDetails({ ...details, displayName: e.target.value })} className={detailSubmitted && detailErrors.displayName ? inputErrClass : inputClass} />
                  <p className="text-[10px] text-slate-500 mt-2">This is how your name will appear in the community and discussions.</p>
                  {detailSubmitted && detailErrors.displayName && <p className="text-[#F43F5E] text-xs mt-1.5">{detailErrors.displayName}</p>}
                </div>

                <div>
                  <label className={labelClass}>Email Address <span className="text-[#F43F5E]">*</span></label>
                  <input type="email" value={details.email} readOnly className={detailSubmitted && detailErrors.email ? inputErrClass : inputClass} />
                  {detailSubmitted && detailErrors.email && <p className="text-[#F43F5E] text-xs mt-1.5">{detailErrors.email}</p>}
                </div>

                <div className="pt-6">
                  <button type="submit" className="bg-[#2563EB] text-white px-8 py-4 rounded-xl font-bold hover:bg-[#1d4ed8] hover:shadow-[0_0_20px_rgba(37,99,235,0.4)] transition-all">
                    Save Changes
                  </button>
                  {profileMessage && <p className="text-slate-400 text-xs mt-3">{profileMessage}</p>}
                </div>
              </form>
            </div>
          )}

          {/* TAB: CHANGE PASSWORD */}
          {activeTab === 'Password' && (
            <div className="space-y-8 animate-in fade-in duration-300">
              <h2 className="text-3xl font-bold text-white text-left">Security Settings</h2>

              <form className="space-y-6" onSubmit={handlePassSubmit} noValidate>

                <div>
                  <label className={labelClass}>Current Password</label>
                  <input type="password" value={passwords.current} onChange={(e) => setPasswords({ ...passwords, current: e.target.value })} placeholder="Enter current password" className={passSubmitted && passErrors.current ? `${inputErrClass} border-[#F43F5E]/60` : `${inputClass}`} />
                  {passSubmitted && passErrors.current && <p className="text-[#F43F5E] text-xs mt-1.5">{passErrors.current}</p>}
                </div>

                <div className="pt-4">
                  <label className={labelClass}>New Password</label>
                  <input type="password" value={passwords.newPass} onChange={(e) => setPasswords({ ...passwords, newPass: e.target.value })} placeholder="Enter new password" className={passSubmitted && passErrors.newPass ? inputErrClass : inputClass} />
                  {passSubmitted && passErrors.newPass && <p className="text-[#F43F5E] text-xs mt-1.5">{passErrors.newPass}</p>}
                </div>

                <div>
                  <label className={labelClass}>Confirm New Password</label>
                  <input type="password" value={passwords.confirm} onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })} placeholder="Confirm new password" className={passSubmitted && passErrors.confirm ? inputErrClass : inputClass} />
                  {passSubmitted && passErrors.confirm && <p className="text-[#F43F5E] text-xs mt-1.5">{passErrors.confirm}</p>}
                </div>

                <div className="pt-6">
                  <button type="submit" disabled={isPasswordLoading} className="bg-[#2563EB] text-white px-8 py-4 rounded-xl font-bold hover:bg-[#1d4ed8] hover:shadow-[0_0_20px_rgba(37,99,235,0.4)] transition-all disabled:opacity-70 disabled:cursor-not-allowed">
                    {isPasswordLoading ? 'Updating Password...' : 'Update Password'}
                  </button>
                  {passMessage && <p className="text-slate-400 text-xs mt-3">{passMessage}</p>}
                </div>
              </form>
            </div>
          )}

        </div>
      </div>

      <Footer />
    </div>
  );
}
